import { useCallback, useEffect, useMemo, useState } from "react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { CountryBadge } from "@/components/go/CountryBadge";
import {
  COUNTRY_PASSPORT_FIELDS,
  formatPassportValue,
} from "@/components/go/countryPassportFields";
import {
  GoCardFieldControl,
  type GoCardFieldCommitResult,
} from "@/components/go/GoCardFieldControl";
import { GoFieldProvenanceControl } from "@/components/go/GoFieldProvenanceControl";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { goEntitiesApi } from "@/services/go-entities.service";
import type { GoMaterialItem } from "@/types/go-entity";
import type { GoEntityCardFieldValue, GoFieldProvenancePatch } from "@/types/go-entity-card";
import type { File as ApiFile } from "@/types/api-v2";
import { cn } from "@/lib/utils";

type Props = {
  name?: string | null;
  ginfObjId?: number | null;
  isoHint?: string | null;
  entityTypeName?: string | null;
  mapGeometryCount?: number;
  fieldValues?: GoEntityCardFieldValue[];
  /** Материалы сущности (для крупных flag/coat); иначе панель подгрузит сама. */
  materials?: GoMaterialItem[] | null;
  templateName?: string | null;
  loading?: boolean;
  editing?: boolean;
  highlightedFieldKey?: string | null;
  savingFieldKeys?: ReadonlySet<string>;
  onCommit?: (fieldKey: string, value: unknown) => Promise<GoCardFieldCommitResult | void>;
  onMarkNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onClearNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onSaveProvenance?: (
    fieldKey: string,
    patch: GoFieldProvenancePatch,
  ) => Promise<GoCardFieldCommitResult | void>;
  onFocusField?: (fieldKey: string) => void;
};

function toPreviewFile(item: GoMaterialItem, ginfObjId: number): ApiFile {
  const name = item.name?.trim() || `material-${item.ginf_file_id}`;
  return {
    id: item.ginf_file_id,
    object_id: ginfObjId,
    name,
    path: "",
    mimetype: item.content_type?.trim() || "image/png",
    size: item.size ?? 0,
    created_at: new Date(0).toISOString(),
  };
}

function SymbolTile({
  ginfObjId,
  item,
  label,
  onOpen,
}: {
  ginfObjId: number;
  item: GoMaterialItem;
  label: string;
  onOpen: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const blob = await goEntitiesApi.downloadMaterialBlob(ginfObjId, item.ginf_file_id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ginfObjId, item.ginf_file_id]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group flex w-[7.5rem] flex-col items-center gap-1 rounded-md border border-border bg-muted/20 p-2",
        "hover:bg-muted/40 hover:ring-2 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "cursor-zoom-in",
      )}
      title={`Просмотр: ${label}`}
      aria-label={`Просмотр: ${label}`}
    >
      <div className="flex h-20 w-full items-center justify-center overflow-hidden rounded-sm bg-background/60">
        {url ? (
          <img src={url} alt={label} className="max-h-20 max-w-full object-contain" />
        ) : (
          <Skeleton className="h-16 w-full" />
        )}
      </div>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground group-hover:text-foreground">
        {label}
      </span>
    </button>
  );
}

/**
 * Паспорт страны для оператора: ключевые факты, без стены полей ok20.
 */
export function CountryPassportPanel({
  name,
  ginfObjId,
  isoHint,
  entityTypeName,
  mapGeometryCount,
  fieldValues = [],
  materials: materialsProp,
  templateName,
  loading,
  editing = false,
  highlightedFieldKey,
  savingFieldKeys,
  onCommit,
  onMarkNa,
  onClearNa,
  onSaveProvenance,
  onFocusField,
}: Props) {
  const byKey = new Map(fieldValues.map((f) => [f.field_key, f]));
  const [localMaterials, setLocalMaterials] = useState<GoMaterialItem[] | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<ApiFile[]>([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  useEffect(() => {
    if (materialsProp != null || ginfObjId == null) {
      setLocalMaterials(null);
      return;
    }
    let cancelled = false;
    goEntitiesApi
      .listMaterials(ginfObjId)
      .then((rows) => {
        if (!cancelled) setLocalMaterials(rows);
      })
      .catch(() => {
        if (!cancelled) setLocalMaterials([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ginfObjId, materialsProp]);

  const materials = materialsProp ?? localMaterials ?? [];

  const flagItem = useMemo(
    () => materials.find((m) => m.material_kind === "flag") ?? null,
    [materials],
  );
  const coatItem = useMemo(
    () => materials.find((m) => m.material_kind === "coat") ?? null,
    [materials],
  );

  const fetchBlob = useCallback(
    (fileId: number) => {
      if (ginfObjId == null) return Promise.reject(new Error("ginf_obj_id required"));
      return goEntitiesApi.downloadMaterialBlob(ginfObjId, fileId);
    },
    [ginfObjId],
  );

  const openSymbolPreview = useCallback(
    (item: GoMaterialItem) => {
      if (ginfObjId == null) return;
      const pair = [flagItem, coatItem].filter(Boolean) as GoMaterialItem[];
      const list = pair.length > 0 ? pair : [item];
      const files = list.map((m) => toPreviewFile(m, ginfObjId));
      const idx = Math.max(
        0,
        list.findIndex((m) => m.ginf_file_id === item.ginf_file_id),
      );
      setPreviewFiles(files);
      setPreviewInitialIndex(idx);
      setPreviewOpen(true);
    },
    [coatItem, flagItem, ginfObjId],
  );

  const resolveRow = (fieldKey: string, altKeys?: string[]) => {
    const keys = [fieldKey, ...(altKeys ?? [])];
    for (const k of keys) {
      const row = byKey.get(k);
      if (!row) continue;
      const text = formatPassportValue(row.value, row.is_masked);
      if (text !== "—") return { row, text, masked: !!row.is_masked };
    }
    if (fieldKey === "1.1.4.1." && isoHint) {
      return { row: null, text: isoHint, masked: false };
    }
    const primary = byKey.get(fieldKey) ?? null;
    return { row: primary, text: "—", masked: false };
  };

  const isoDisplay = (() => {
    const r = resolveRow("1.1.4.1.", ["iso_alpha3"]);
    return r.text !== "—" ? r.text : isoHint ?? null;
  })();

  const hasSymbols = !!(flagItem || coatItem) && ginfObjId != null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
            Паспорт страны
            <SectionHelpTooltip label="Справка: паспорт Country Hub">
              <p className="font-medium">Зачем</p>
              <p>
                Компактный обзор для оператора: имя, ISO, столица и ключевые показатели — без сотен
                полей ok20.
              </p>
              <p className="mt-2 font-medium">Как работать</p>
              <ul className="list-disc pl-4">
                <li>Крупные флаг и герб справа — клик открывает просмотр (как во вкладке Материалы).</li>
                <li>
                  Иконка принтера у заголовка «Общие сведения» — печать PDF (паспорт, символика,
                  схема контура).
                </li>
                <li>Детали по блокам — вкладки География / Общество / Экономика или «Все поля».</li>
                <li>
                  «Редактировать карточку» — правка whitelist-полей; blur сохраняет. Esc — выход из
                  правки, повторный Esc — снять выбор.
                </li>
                <li>N/A — кнопка у поля или Ctrl+Shift+N; снятие — «Снять N/A».</li>
                <li>Импорт пакетов — админка ГО, не тулбар workspace.</li>
              </ul>
            </SectionHelpTooltip>
          </div>
          <CountryBadge name={name} iso={isoDisplay} ginfObjId={ginfObjId ?? undefined} />
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {entityTypeName ? <span>{entityTypeName}</span> : null}
            {mapGeometryCount != null ? <span>Геометрий: {mapGeometryCount}</span> : null}
            {templateName ? <span className="truncate max-w-[240px]">Шаблон: {templateName}</span> : null}
            {editing ? <span className="text-foreground">режим правки</span> : null}
          </div>
        </div>

        {hasSymbols ? (
          <div className="flex shrink-0 flex-wrap items-start gap-2">
            {flagItem ? (
              <SymbolTile
                ginfObjId={ginfObjId!}
                item={flagItem}
                label="Флаг"
                onOpen={() => openSymbolPreview(flagItem)}
              />
            ) : null}
            {coatItem ? (
              <SymbolTile
                ginfObjId={ginfObjId!}
                item={coatItem}
                label="Герб"
                onOpen={() => openSymbolPreview(coatItem)}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Загрузка паспорта…</div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {COUNTRY_PASSPORT_FIELDS.map((def) => {
            const { row, text, masked } = resolveRow(def.fieldKey, def.altKeys);
            if (editing && row && !row.is_masked && !row.is_readonly) {
              return (
                <div
                  key={def.fieldKey}
                  className="rounded-md border border-border bg-muted/20 px-3 py-2"
                >
                  <GoCardFieldControl
                    field={{ ...row, label: def.label }}
                    editing
                    highlighted={highlightedFieldKey === row.field_key}
                    saving={savingFieldKeys?.has(row.field_key)}
                    onCommit={onCommit}
                    onMarkNa={onMarkNa}
                    onClearNa={onClearNa}
                    onSaveProvenance={onSaveProvenance}
                    onFocusField={onFocusField}
                  />
                </div>
              );
            }
            const showNa = !!row?.is_na;
            return (
              <div
                key={def.fieldKey}
                data-field-key={row?.field_key ?? def.fieldKey}
                className={`rounded-md border border-border bg-muted/20 px-3 py-2 ${
                  highlightedFieldKey === (row?.field_key ?? def.fieldKey)
                    ? "ring-2 ring-primary/60 ring-offset-2 ring-offset-background"
                    : ""
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {def.label}
                    {showNa ? <span className="ml-1 normal-case">⊘ N/A</span> : null}
                  </div>
                  {row && !row.is_masked ? (
                    <GoFieldProvenanceControl field={row} editing={false} />
                  ) : null}
                </div>
                <div
                  className={`mt-0.5 text-sm text-foreground break-words ${masked ? "tracking-widest" : ""}`}
                  title={!showNa && text !== "—" ? text : undefined}
                >
                  {showNa ? "⊘ Неприменимо" : text}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {ginfObjId != null ? (
        <FilePreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          fetchBlob={fetchBlob}
          files={previewFiles}
          initialIndex={previewInitialIndex}
        />
      ) : null}
    </div>
  );
}
